import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Copy, Check, FileDown, Sparkles } from "lucide-react";
import { toast } from "sonner";
import type { PeerReviewSummary } from "@/lib/smartessay.functions";
import { disclaimerPdfFooter, disclaimerTextCopy } from "@/lib/disclaimers";
import { AcademicDisclaimer } from "@/components/AcademicDisclaimer";
import {
  AcademicConsentDialog,
  useAcademicConsent,
} from "@/components/AcademicConsentGate";

const SECTION_LABELS: Record<keyof PeerReviewSummary["sections"], string> = {
  introduction: "Introduction",
  subjects: "Subjects / Participants",
  instrument: "Overview of the Measurement Instrument",
  results: "Results",
  critique: "Study Critique",
};

function formatForDocs(s: PeerReviewSummary): string {
  let out = "";
  // APA 7 student title page block
  out += s.author_block.join("\n") + "\n\n";
  out += s.title + "\n\n";
  out += "Reference\n";
  out += s.article_citation + "\n\n";
  (Object.keys(SECTION_LABELS) as (keyof typeof SECTION_LABELS)[]).forEach((k) => {
    out += SECTION_LABELS[k] + "\n";
    out += s.sections[k].trim() + "\n\n";
  });
  out += disclaimerTextCopy();
  return out.trim();
}

export function PeerReviewView({ summary }: { summary: PeerReviewSummary }) {
  const [copied, setCopied] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const consent = useAcademicConsent();

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(formatForDocs(summary));
      setCopied(true);
      toast.success("Copied — paste into Word or Google Docs");
      setTimeout(() => setCopied(false), 2500);
    } catch {
      toast.error("Couldn't copy to clipboard.");
    }
  }

  async function downloadPdf() {
    setPdfLoading(true);
    try {
      const { jsPDF } = await import("jspdf");
      const doc = new jsPDF({ unit: "in", format: "letter" });
      const pageW = doc.internal.pageSize.getWidth();
      const pageH = doc.internal.pageSize.getHeight();
      const margin = 1;
      const usableW = pageW - margin * 2;
      const lineHeight = 0.32;

      doc.setFont("times", "normal");
      doc.setFontSize(12);

      let y = margin;
      const ensure = (n = lineHeight) => {
        if (y + n > pageH - margin) {
          doc.addPage();
          y = margin;
        }
      };
      const writeLine = (
        text: string,
        opts?: { align?: "left" | "center"; bold?: boolean; hangingIndent?: boolean }
      ) => {
        doc.setFont("times", opts?.bold ? "bold" : "normal");
        const wrapped = doc.splitTextToSize(text, usableW - (opts?.hangingIndent ? 0.5 : 0));
        wrapped.forEach((ln: string, i: number) => {
          ensure();
          if (opts?.align === "center") {
            doc.text(ln, pageW / 2, y, { align: "center" });
          } else if (opts?.hangingIndent) {
            const x = i === 0 ? margin : margin + 0.5;
            doc.text(ln, x, y);
          } else {
            doc.text(ln, margin, y);
          }
          y += lineHeight;
        });
      };
      const writePara = (text: string) => {
        doc.setFont("times", "normal");
        const wrapped = doc.splitTextToSize(text, usableW);
        wrapped.forEach((ln: string, j: number) => {
          ensure();
          const x = j === 0 ? margin + 0.5 : margin;
          doc.text(ln, x, y);
          y += lineHeight;
        });
        y += lineHeight * 0.5;
      };

      // APA 7 student title page
      y = pageH / 3;
      writeLine(summary.title, { align: "center", bold: true });
      y += lineHeight;
      for (const line of summary.author_block) writeLine(line, { align: "center" });

      // Body page
      doc.addPage();
      y = margin;
      writeLine(summary.title, { align: "center", bold: true });
      y += lineHeight * 0.5;

      const order: (keyof PeerReviewSummary["sections"])[] = [
        "introduction",
        "subjects",
        "instrument",
        "results",
        "critique",
      ];
      for (const k of order) {
        ensure(lineHeight * 2);
        writeLine(SECTION_LABELS[k], { align: "center", bold: true });
        y += lineHeight * 0.25;
        const paras = summary.sections[k].split(/\n\s*\n/);
        for (const p of paras) writePara(p.trim());
      }

      // References page
      doc.addPage();
      y = margin;
      writeLine("References", { align: "center", bold: true });
      y += lineHeight * 0.5;
      writeLine(summary.article_citation, { hangingIndent: true });

      // Page numbers top-right + integrity footer on every page
      const total = doc.getNumberOfPages();
      const footerText = disclaimerPdfFooter();
      for (let p = 1; p <= total; p++) {
        doc.setPage(p);
        doc.setFont("times", "normal");
        doc.setFontSize(12);
        doc.text(`${p}`, pageW - margin, margin - 0.4, { align: "right" });

        doc.setFontSize(8);
        doc.setTextColor(120, 120, 120);
        doc.text(footerText, pageW / 2, pageH - 0.4, { align: "center", maxWidth: usableW });
        doc.setTextColor(0, 0, 0);
        doc.setFontSize(12);
      }

      const safe = summary.title.replace(/[^a-z0-9]+/gi, "_").slice(0, 60) || "peer_review_summary";
      doc.save(`${safe}_APA.pdf`);
      toast.success("APA 7 summary downloaded");
    } catch (err) {
      toast.error((err as Error).message || "Couldn't build PDF.");
    } finally {
      setPdfLoading(false);
    }
  }

  return (
    <section className="mt-8 rounded-2xl border border-border bg-card p-6 shadow-soft sm:p-8">
      <AcademicDisclaimer variant="banner" collapsible className="mb-4" />
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div className="inline-flex items-center gap-2 rounded-full bg-accent px-3 py-1 text-xs text-accent-foreground">
          <Sparkles className="h-3.5 w-3.5" /> Peer-reviewed article summary (APA 7)
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={handleCopy} className="gap-1.5">
            {copied ? (<><Check className="h-3.5 w-3.5" /> Copied</>) : (<><Copy className="h-3.5 w-3.5" /> Copy for Word / Docs</>)}
          </Button>
          <Button
            size="sm"
            onClick={() => consent.requireConsent(() => { void downloadPdf(); })}
            disabled={pdfLoading}
          >
            <FileDown className="mr-1.5 h-4 w-4" />
            {pdfLoading ? "Building…" : "Download APA PDF"}
          </Button>
        </div>
      </div>

      <h2 className="font-display text-xl font-semibold">{summary.title}</h2>
      <div className="mt-1 text-xs text-muted-foreground whitespace-pre-line">
        {summary.author_block.join(" · ")}
      </div>

      <div className="mt-6 rounded-lg border border-dashed border-border p-4">
        <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Reference</div>
        <p className="mt-1 text-sm leading-relaxed">{summary.article_citation}</p>
      </div>

      <div className="mt-6 space-y-6">
        {(Object.keys(SECTION_LABELS) as (keyof typeof SECTION_LABELS)[]).map((k) => (
          <article key={k}>
            <h3 className="font-display text-base font-semibold">{SECTION_LABELS[k]}</h3>
            <p className="mt-1 whitespace-pre-line text-sm leading-relaxed text-foreground">
              {summary.sections[k]}
            </p>
          </article>
        ))}
      </div>

      <AcademicConsentDialog
        open={consent.open}
        onOpenChange={consent.setOpen}
        onAccept={consent.accept}
      />
    </section>
  );
}
